import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { api } from "../lib/api";
import { logError } from "../lib/log";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const VISIBLE = 8;

export const Gallery = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch((e) => logError("Gallery load failed", e));
  }, []);

  const openAt = (i) => { setIdx(i); setOpen(true); };
  const close = () => setOpen(false);
  const prev = useCallback(() => setIdx((i) => (i - 1 + items.length) % items.length), [items.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % items.length), [items.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next]);

  const visible = items.slice(0, VISIBLE);
  const remaining = items.length - VISIBLE;

  return (
    <section id="galerie" data-testid="gallery-section" className="py-24 md:py-32 lg:py-40 relative">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mb-14 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-gold">Galerie</span>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-tight">
              En <span className="gold-text">images</span>.
            </h2>
          </div>
          {items.length > 0 && (
            <span data-testid="gallery-count" className="flex items-baseline gap-2 text-sm text-[#A3A3A3]">
              <span className="font-serif text-3xl gold-text leading-none">{items.length}</span>
              photo{items.length > 1 ? "s" : ""}
            </span>
          )}
        </div>

        {items.length === 0 ? (
          <div data-testid="gallery-empty" className="border border-dashed border-white/[0.12] py-24 text-center">
            <p className="text-[#8A8A8A] font-light">De nouvelles images arrivent bientôt ✦</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {visible.map((item, i) => (
                <motion.button
                  key={item.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                  onClick={() => openAt(i)}
                  data-testid={`gallery-item-${item.id}`}
                  className="group relative aspect-square overflow-hidden border border-white/[0.08]"
                >
                  <img
                    src={`${BACKEND}${item.url}`}
                    alt={item.caption || "Akalibe"}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                  />
                  {i === VISIBLE - 1 && remaining > 0 ? (
                    <div className="absolute inset-0 bg-black/65 flex flex-col items-center justify-center gap-1">
                      <span className="font-serif text-3xl text-white">+{remaining}</span>
                      <span className="text-[10px] uppercase tracking-[0.25em] text-gold">Voir tout</span>
                    </div>
                  ) : (
                    item.caption && (
                      <figcaption className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-base/90 to-transparent text-xs text-white/90 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-400">
                        {item.caption}
                      </figcaption>
                    )
                  )}
                  <div className="absolute inset-0 ring-1 ring-inset ring-gold/0 group-hover:ring-gold/40 transition-all duration-400" />
                </motion.button>
              ))}
            </div>

            {items.length > VISIBLE && (
              <div className="mt-10 text-center">
                <button
                  data-testid="gallery-view-all"
                  onClick={() => openAt(0)}
                  className="inline-flex items-center gap-2.5 border border-gold text-gold px-8 py-4 hover:bg-gold/10 transition-colors duration-300 text-sm tracking-wide"
                >
                  <Images className="w-4 h-4" /> Voir les {items.length} photos
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open && items[idx] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
            onClick={close}
          >
            <span className="absolute top-6 left-6 text-sm text-white/70 tracking-wide">
              {idx + 1} / {items.length}
            </span>
            <button
              data-testid="lightbox-close"
              onClick={close}
              aria-label="Fermer"
              className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center border border-white/20 text-white/80 hover:border-gold hover:text-gold transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Précédent"
              className="absolute left-3 sm:left-6 w-12 h-12 flex items-center justify-center border border-white/20 text-white/80 hover:border-gold hover:text-gold hover:bg-black/40 transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <motion.img
              key={items[idx].id}
              src={`${BACKEND}${items[idx].url}`}
              alt={items[idx].caption || "Akalibe"}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
              className="max-h-[85vh] max-w-[90vw] object-contain shadow-[0_20px_80px_rgba(0,0,0,0.6)]"
            />

            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Suivant"
              className="absolute right-3 sm:right-6 w-12 h-12 flex items-center justify-center border border-white/20 text-white/80 hover:border-gold hover:text-gold hover:bg-black/40 transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {items[idx].caption && (
              <p className="absolute bottom-6 inset-x-0 text-center text-sm text-white/80 px-6">
                {items[idx].caption}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
